import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";
import type { Role } from "./adminRoleService";
import type { Participant } from "./adminParticipantService";

export interface User {
    user_id: number;
    niu: string | null;
    nip?: string | null;
    role_id: number | null;
    participant_id: number | null;
    event_guest_id: number | null;
    name: string;
    email: string;
    is_active: boolean;
    role?: Role;
    participant?: Participant;
    eventGuest?: any;
    created_at?: string;
    updated_at?: string;
}

export type UserPayload = Omit<User, "user_id" | "role" | "participant" | "eventGuest" | "created_at" | "updated_at"> & {
    password?: string;
};

// GET All Users
export const useAdminUsers = () => {
    return useQuery<User[]>({
        queryKey: ["admin-users"],
        queryFn: async () => {
            const { data } = await axiosClient.get("/users", { withCredentials: true });
            return data;
        },
    });
};

// GET Single User
export const useAdminUser = (id: number | null) => {
    return useQuery<User>({
        queryKey: ["admin-user", id],
        queryFn: async () => {
            const { data } = await axiosClient.get(`/users/${id}`, { withCredentials: true });
            return data;
        },
        enabled: !!id,
    });
};

// CREATE User
export const useCreateUser = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: UserPayload) => {
            const { data } = await axiosClient.post("/users", payload, { withCredentials: true });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-users"] });
        },
    });
};

// UPDATE User
export const useUpdateUser = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: number; payload: Partial<UserPayload> }) => {
            const { data } = await axiosClient.put(`/users/${id}`, payload, { withCredentials: true });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-users"] });
        },
    });
};

// DELETE User
export const useDeleteUser = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            const { data } = await axiosClient.delete(`/users/${id}`, { withCredentials: true });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-users"] });
        },
    });
};

// RESET Password Default
export const useResetPasswordDefault = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            const { data } = await axiosClient.post(`/users/${id}/reset-password-default`, {}, { withCredentials: true });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-users"] });
        },
    });
};
// GET Conference Access
export const useGetConferenceAccess = (id: number | null) => {
    return useQuery<number[]>({
        queryKey: ["conference-access", id],
        queryFn: async () => {
            const { data } = await axiosClient.get(`/users/${id}/conference-access`, { withCredentials: true });
            return data.data;
        },
        enabled: !!id,
    });
};

// UPDATE Conference Access
export const useUpdateConferenceAccess = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, conference_ids }: { id: number; conference_ids: number[] }) => {
            const { data } = await axiosClient.post(`/users/${id}/conference-access`, { conference_ids }, { withCredentials: true });
            return data;
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ["conference-access", variables.id] });
        },
    });
};
